// Middleware API client.
//
// Auth: bearer token from window.CC_WIDGET_CONFIG (server-injected in production).
// Endpoint shapes match ~/clubchampion/middleware/src/api/ exactly.
//
// Availability is the trickiest piece: the middleware's /availability route
// is per-fitter (resourceId + ISO date range + durationMin). For the v2 widget's
// "Any Fitter" UX and cross-location fan-out (ADR 0003), the client fans out
// in parallel across fitters and merges the results. The middleware stays
// simple; the client orchestrates.

import type {
  Location,
  Resource,
  BookingPayload,
  BookingResponse,
  ValidatePromoResponse,
  WaitlistPayload,
  PostBookingPayload,
  LookedUpAppointment,
  ReschedulePayload,
} from '../types';
import {
  DEFAULT_FITTER_ASSIGNMENT,
  pickFitterForSlot,
  type FitterAssignment,
} from '../utils/fitterAssignment';
import type { ZipGeocode } from '../utils/geocode';
import { getWafToken } from './waf-token';

/**
 * Resolve the API base URL + bearer, host-page config first, then any
 * build-time defaults baked into a "hardcoded" build (the prod/staging CDN
 * bundles set VITE_CC_DEFAULT_*). Returns null instead of throwing, for
 * fire-and-forget callers (the lead vault + the account seed) that must never
 * break the booking flow.
 *
 * This is the ONE place API config is resolved. seedAccount()/postLead() in
 * analytics/lead-post.ts MUST use it too — they used to read
 * CC_WIDGET_CONFIG.apiBaseUrl directly with no build-time fallback, so on host
 * pages that inject only analytics config (every international site:
 * {sgtmUrl, ga4MeasurementId, embedSource}) the seed silently no-op'd while
 * bookings still went out on the baked-in default. That dropped the entire
 * international Step-1 funnel.
 */
export function resolveApiConfigOrNull(): { apiBaseUrl: string; bearerToken: string } | null {
  const c = window.CC_WIDGET_CONFIG ?? {};
  const apiBaseUrl = c.apiBaseUrl || import.meta.env.VITE_CC_DEFAULT_API_BASE_URL;
  const bearerToken = c.bearerToken || import.meta.env.VITE_CC_DEFAULT_BEARER;
  if (!apiBaseUrl || !bearerToken) return null;
  return { apiBaseUrl: apiBaseUrl.replace(/\/$/, ''), bearerToken };
}

const config = (): { apiBaseUrl: string; bearerToken: string } => {
  const resolved = resolveApiConfigOrNull();
  if (!resolved) {
    // Booking must fail loudly — unlike the fire-and-forget seed, a missing
    // base URL here means the widget cannot function at all.
    throw new Error(
      '[cc-widget] window.CC_WIDGET_CONFIG.apiBaseUrl / bearerToken is not set, ' +
        'and no build-time default is baked in. The host page must inject ' +
        'runtime config (and mint a per-session token server-side) before the ' +
        'widget loads.',
    );
  }
  return resolved;
};

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl, bearerToken } = config();
  const url = `${apiBaseUrl}${path}`;
  const method = (init?.method ?? 'GET').toUpperCase();
  const requestBody =
    typeof init?.body === 'string' ? init.body : init?.body ? String(init.body) : undefined;
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${bearerToken}`);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // When CC_WIDGET_CONFIG.wafIntegrationUrl is set, attach the WAF token
  // so CaptchaOnSessionMint + CaptchaOnBookingCreate rules pass without
  // serving a CAPTCHA page to legit users. When unset (current prod),
  // getWafToken() returns null and we send the request as before.
  const wafToken = getWafToken();
  if (wafToken) headers.set('X-Aws-Waf-Token', wafToken);

  const startedAt = performance.now();
  const traceId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers });
  } catch (err) {
    emitTrafficEvent({
      id: traceId,
      method,
      path,
      status: 0,
      durationMs: Math.round(performance.now() - startedAt),
      requestBody,
      responseBody: err instanceof Error ? err.message : String(err),
      ok: false,
    });
    throw err;
  }

  const durationMs = Math.round(performance.now() - startedAt);
  const text = await response.text().catch(() => '');

  emitTrafficEvent({
    id: traceId,
    method,
    path,
    status: response.status,
    durationMs,
    requestBody,
    responseBody: text,
    ok: response.ok,
  });

  if (!response.ok) {
    throw new ApiError(response.status, `${response.status} ${response.statusText}`, text);
  }

  // Empty 204 responses have no body — return undefined to satisfy the caller's
  // Promise<T>. Callers that expect a value won't await void endpoints.
  if (!text) return undefined as unknown as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}

// ─── Traffic instrumentation (for the in-widget TRAFFIC drawer) ────────────

export interface TrafficEntry {
  id: string;
  timestamp: number;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  requestBody?: string;
  responseBody?: string;
  ok: boolean;
}

function emitTrafficEvent(entry: Omit<TrafficEntry, 'timestamp'>): void {
  const full: TrafficEntry = { ...entry, timestamp: Date.now() };
  window.dispatchEvent(new CustomEvent('cc-api-traffic', { detail: full }));
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Pull a user-facing message out of a failed API response body. The Schedule
// endpoint (relayed by the middleware) returns { isSuccess:false, message, ... }
// on a business rejection — e.g. a double-booking's "Customer already has a
// future appointment scheduled" — which is worth showing verbatim.
// Returns null when there's no parseable message, or when what is there is a
// diagnostic rather than a sentence (see looksInternal), so callers keep their
// fallback.
/**
 * Some of what the middleware puts in `message` is a diagnostic, not a
 * sentence: CC's Canada review caught a customer being shown
 *
 *   CC API POST /services/apexrest/api/GolfNow/Schedule → 500
 *
 * on a booking that failed because they already had an appointment. It reads
 * as a crash, and it publishes an internal endpoint to anyone who can make a
 * booking fail.
 *
 * The test is deliberately about shape rather than a list of known strings — a
 * message naming an HTTP verb, a URL path, an arrow-to-status, or a bare status
 * code is machine output whatever it says. Anything that fails this is treated
 * as unusable and the caller falls back to its own wording.
 */
function looksInternal(message: string): boolean {
  return (
    /\b(GET|POST|PUT|PATCH|DELETE)\b\s+\//.test(message)
    || /\/(services|apexrest|api)\//i.test(message)
    || /[→>]\s*\d{3}\b/.test(message)
    || /\b(?:HTTP\s*)?[45]\d{2}\b(?!\s*[a-z])/.test(message)
    || /\bundefined\b|\bnull\b|\[object /.test(message)
    || /<\/?[a-z]/i.test(message)
  );
}

export function apiErrorMessage(err: unknown): string | null {
  const raw = err instanceof ApiError ? err.body : undefined;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      const message = parsed.message.trim();
      return looksInternal(message) ? null : message;
    }
  } catch {
    // body wasn't JSON — nothing to extract
  }
  return null;
}

/**
 * A gateway that gave up waiting, rather than a booking that was refused.
 *
 * Observed in production 2026-08-20: a POST /bookings reached the middleware,
 * logged its request, and never produced a response. The gateway timed out and
 * returned a 504 whose body is a CloudFront HTML error page — which the widget
 * then printed at the customer, tags and all.
 *
 * The message matters more than the tidiness. Salesforce had already created
 * the appointment: every retry came back 409 "you already have a fitting
 * appointment scheduled". So a timeout does NOT mean the booking failed — it
 * means we do not know, and telling somebody it failed invites them to make a
 * second one they cannot make.
 *
 * Returns null for anything that is a real answer from the booking system.
 */
export function gatewayTimeoutMessage(err: unknown): string | null {
  const timedOut =
    (err instanceof ApiError && (err.status === 502 || err.status === 503 || err.status === 504)) ||
    // fetch() rejects rather than resolving when the connection is cut.
    (err instanceof TypeError && /fetch|network/i.test(err.message));
  if (!timedOut) return null;

  return (
    'We didn\u2019t hear back from the booking system in time, so we can\u2019t confirm ' +
    'whether your appointment was created. Please check your email for a confirmation ' +
    'before booking again \u2014 if nothing arrives in the next few minutes, call the studio ' +
    'and we\u2019ll sort it out.'
  );
}

/**
 * The same conflict, told to somebody whose previous attempt timed out.
 *
 * Without this they are told they already have an appointment moments after
 * being told their booking failed, which reads as a system that has lost track
 * of them. It has not — the first attempt worked.
 */
export function doubleBookingAfterTimeoutMessage(): string {
  return (
    'Good news \u2014 your appointment was already booked. The earlier attempt did go ' +
    'through despite the error, so there is nothing more to do. Check your email for ' +
    'the confirmation.'
  );
}

// Double-booking detection, done widget-side because the middleware relays the
// Salesforce Schedule 409 as a generic "CC API POST …/GolfNow/Schedule → 409"
// descriptor rather than Salesforce's own message. The Schedule endpoint returns
// 409 specifically when the customer already has a future appointment, so a 409
// whose body names that endpoint IS the double-booking. Other 409s (notably the
// middleware's own "Requested slot is not currently available" for a taken slot)
// don't match and keep their own message. Returns null when it's not this case.
export function doubleBookingMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  if (!/GolfNow\/Schedule/i.test(err.body ?? '')) return null;
  return 'You already have a fitting appointment scheduled.';
}

// ─── Reads ──────────────────────────────────────────────────────────────────

/**
 * @param country Optional ISO 2-letter code ('us' | 'uk' | 'ca' | 'au') or
 *   the literal 'all' to bypass country filtering. Omit to accept the
 *   middleware's default (currently 'us' for backwards compatibility with
 *   pre-country widgets).
 */
export async function listLocations(country?: string | null): Promise<Location[]> {
  const c = country?.trim().toLowerCase();
  const path = c ? `/locations?country=${encodeURIComponent(c)}` : '/locations';
  const { locations } = await apiFetch<{ locations: Location[] }>(path);
  return locations;
}

/**
 * Resolve a typed place name to coordinates, via the middleware.
 *
 * Only called when the input is neither a postcode nor one of the metro names
 * the widget already knows, so this is the long tail — suburbs, towns, the
 * places a curated list was never going to cover. CC's Australian team hit
 * exactly this: postcode search worked, and typing any city other than the
 * store's own returned nothing.
 *
 * Resolves to null on any failure, including a 404 for an unresolvable query.
 * Callers fall back to text matching, which is what studio search did before
 * this existed — a geocoder being unavailable must narrow the search, not
 * break it.
 *
 * @see middleware/src/services/geocode.service.ts
 */
export async function geocodePlaceName(
  query: string,
  country: string | null | undefined,
): Promise<ZipGeocode | null> {
  const q = query.trim();
  const c = (country ?? '').trim().toLowerCase();
  if (q.length < 3 || c.length !== 2) return null;
  try {
    return await apiFetch<ZipGeocode>(
      `/geocode?q=${encodeURIComponent(q)}&country=${encodeURIComponent(c)}`,
    );
  } catch {
    return null;
  }
}

/**
 * The master promotion code for each market, read from Salesforce metadata.
 *
 * These differ per Salesforce org — the QA org runs a different US code to
 * production — so they cannot be constants compiled into the bundle. Fetched
 * once per page load and shared, since the widget may be engaged more than
 * once and the value does not change within a session.
 *
 * Resolves to an empty map on any failure. Callers fall back to their
 * per-country default: a promotion code is an optimisation on the price, and
 * failing to read one must never stop somebody booking a fitting.
 */
let masterPromoPromise: Promise<Record<string, string>> | null = null;

export function getMasterPromoCodes(): Promise<Record<string, string>> {
  if (!masterPromoPromise) {
    masterPromoPromise = apiFetch<{ codes: Record<string, string> }>('/promos/master')
      .then((r) => r.codes ?? {})
      .catch(() => ({}));
  }
  return masterPromoPromise;
}

export async function listResources(): Promise<Resource[]> {
  const { resources } = await apiFetch<{ resources: Resource[] }>('/resources');
  return resources;
}

/**
 * A single fitting service offered by a store, as returned by the middleware's
 * /fittings endpoint. `schedulingAppName` is the wire identifier we must send
 * on Availability + Schedule + Waitlist — it carries country suffixes for
 * international stores ("Wedge UK", "Driver Canada", "Wedge AUS"). The
 * widget's fittingTypes.ts hardcodes the US short names as canonical keys;
 * this response is what we resolve them against per store.
 */
export interface StoreFitting {
  productId: string;
  schedulingAppName: string;
  description: string;
  durationMinutes: number;
  durationLabel: string;
  unitPrice: number;
}

export interface StoreFittingsResponse {
  storeId: string;
  storeName: string;
  fittings: StoreFitting[];
  /**
   * SF-active promo code for the store, sourced from
   * promoRecord.Promotion_Code__c on the raw /Fittings entries.
   * `null` when SF has no active promo attached — widget falls back
   * to the country-config initialPromoCode. This is the promo code
   * that SF's Schedule endpoint will actually recognize; the widget's
   * country-config defaults are only meaningful as a fallback.
   */
  activePromoCode: string | null;
  /**
   * Terms and conditions text for the active promo, sourced from
   * promoRecord.Terms_and_Conditions__c. Populates the widget's
   * "See Terms and Conditions" modal so it reflects the currently-
   * applied promo rather than the hardcoded FITTINGUS default.
   * `null` when SF has no active promo, or the promo record ships
   * without a T&C string.
   */
  activePromoTerms: string | null;
}

export async function listStoreFittings(storeName: string): Promise<StoreFittingsResponse> {
  return apiFetch<StoreFittingsResponse>(
    `/fittings?storeName=${encodeURIComponent(storeName)}`,
  );
}

// ─── Availability (per-fitter + client-side fan-out for "Any Fitter") ──────

export interface MergedSlot {
  start: string;
  end: string;
  fitterId: string;
  fitterName: string;
}

interface MiddlewareAvailabilityResponse {
  resourceId: string;
  range: { start: string; end: string };
  mode: string;
  slots: Array<{ start: string; end: string }>;
}

async function fetchOneFitterAvailability(
  fitter: Resource,
  date: string,
  durationMin: number,
  fittingType?: string,
): Promise<MergedSlot[]> {
  const params = new URLSearchParams({
    resourceId: fitter.id,
    start: date,
    end: date,
    durationMin: String(durationMin),
  });
  if (fittingType) params.set('fittingType', fittingType);
  const res = await apiFetch<MiddlewareAvailabilityResponse>(`/availability?${params}`);
  return res.slots.map((s) => ({
    start: s.start,
    end: s.end,
    fitterId: fitter.id,
    fitterName: fitter.name,
  }));
}

export interface AvailabilityRequest {
  // Either resource (single fitter) OR location (fan out across all fitters there)
  resource?: Resource;
  location?: Location;
  // The cached resource list for fanout — saves a fetch if the caller has it
  knownResources?: Resource[];
  date: string;
  durationMin: number;
  /** Selected fitting type name, forwarded so SF returns slots scoped to it. */
  fittingType?: string;
  /**
   * Location requests only: how a time several fitters share is given to one
   * of them. Defaults to the random pick. See utils/fitterAssignment.ts.
   */
  fitterAssignment?: FitterAssignment;
}

export interface AvailabilityResponse {
  slots: MergedSlot[];
}

/**
 * Get availability for a fitter (single call) or a whole location ("Any Fitter",
 * fans out per-fitter in parallel, merges, sorts by start time, de-dupes).
 */
/**
 * The seed card's NEXT AVAILABLE row: the earliest open slot at a location
 * over the coming week, and how many other openings that week holds.
 *
 * Same per-fitter range fan-out as getAvailableDatesInRange — one middleware
 * call per fitter for the whole window — but keeping the slots rather than
 * collapsing them to dates, because "13 more this week" is a count of
 * bookable times, not of days. De-duped on start time across fitters so a
 * store where every fitter shares the AnyFitter grid does not count the same
 * opening once per fitter.
 */
export async function getNextAvailableSummary(req: {
  location: Location;
  durationMin: number;
  fittingType?: string;
}): Promise<{ firstStart: string; weekSlotCount: number } | null> {
  const fitters = (await listResources()).filter((r) => r.locationId === req.location.id);
  if (fitters.length === 0) return null;

  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const end = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  const results = await Promise.allSettled(
    fitters.map(async (f) => {
      const params = new URLSearchParams({
        resourceId: f.id,
        start: fmt(today),
        end: fmt(end),
        durationMin: String(req.durationMin),
      });
      if (req.fittingType) params.set('fittingType', req.fittingType);
      const res = await apiFetch<MiddlewareAvailabilityResponse>(`/availability?${params}`);
      return res.slots;
    }),
  );

  const starts = new Set<string>();
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const slot of r.value) starts.add(slot.start);
  }
  if (starts.size === 0) return null;

  const sorted = [...starts].sort((a, b) => a.localeCompare(b));
  return { firstStart: sorted[0], weekSlotCount: sorted.length };
}

/**
 * Get unique YYYY-MM-DD dates that have at least one available slot across
 * all fitters at a location, for a given fitting type, over a date range.
 * Used by the Calendar to grey out days with no availability.
 *
 * Fans out across fitters in parallel (Promise.allSettled — a single fitter's
 * failure doesn't kill the batch). Each fitter's response covers the entire
 * range, so this is roughly one middleware call per fitter — typically 5
 * calls total for a multi-month horizon.
 */
export async function getAvailableDatesInRange(req: {
  location: Location;
  startDate: string;
  endDate: string;
  durationMin: number;
  fittingType?: string;
  knownResources?: Resource[];
  /**
   * Filter to a single fitter's availability. When unset, the date set is
   * the union of every fitter at the location ("any fitter" intent). When
   * set, only that fitter's date set is returned — used after the user
   * picks a specific fitter so the calendar shows only days they're working.
   */
  resourceId?: string;
}): Promise<Set<string>> {
  const fitters =
    req.knownResources ??
    (await listResources()).filter((r) => r.locationId === req.location.id);
  const atLoc = req.resourceId
    ? fitters.filter((r) => r.id === req.resourceId)
    : fitters.filter((r) => r.locationId === req.location.id);
  if (atLoc.length === 0) return new Set();

  const results = await Promise.allSettled(
    atLoc.map(async (f) => {
      const params = new URLSearchParams({
        resourceId: f.id,
        start: req.startDate,
        end: req.endDate,
        durationMin: String(req.durationMin),
      });
      if (req.fittingType) params.set('fittingType', req.fittingType);
      const res = await apiFetch<MiddlewareAvailabilityResponse>(`/availability?${params}`);
      return res.slots;
    }),
  );

  return datesFromSlots(results.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])));
}

/**
 * The calendar day a slot belongs to, as the studio sees it.
 *
 * Slot times are the studio's wall clock with a literal `Z` appended
 * ("2026-10-20T13:00:00.000Z" is 1:00 PM at that studio, in any country), so
 * the first ten characters ARE the studio's date. Parsing the string as a
 * real instant and reading the browser's local date moved every afternoon
 * slot in Australia onto the next day: in a Sydney browser (UTC+11) 13:00 "Z"
 * reads as 00:00 the following morning, so the 20th greyed out as "nothing
 * available" while clicking it listed six times (Steve, via Dhruv,
 * 2026-10-20). US browsers never saw it, because UTC-5 keeps every daytime
 * slot on the same date.
 */
export function slotDateKey(start: string): string {
  return start.slice(0, 10);
}

/** The set of studio-local dates that have at least one slot. */
export function datesFromSlots(slots: Array<{ start: string }>): Set<string> {
  const dates = new Set<string>();
  for (const slot of slots) dates.add(slotDateKey(slot.start));
  return dates;
}

export async function getAvailability(req: AvailabilityRequest): Promise<AvailabilityResponse> {
  if (req.resource) {
    const slots = await fetchOneFitterAvailability(req.resource, req.date, req.durationMin, req.fittingType);
    return { slots: slots.sort((a, b) => a.start.localeCompare(b.start)) };
  }

  if (req.location) {
    const fitters =
      req.knownResources ??
      (await listResources()).filter((r) => r.locationId === req.location!.id);

    const atLoc = fitters.filter((r) => r.locationId === req.location!.id);

    if (atLoc.length === 0) return { slots: [] };

    // Parallel fan-out per ADR 0003; settle so one failure doesn't kill the batch
    const results = await Promise.allSettled(
      atLoc.map((f) => fetchOneFitterAvailability(f, req.date, req.durationMin, req.fittingType)),
    );
    const allSlots = results
      .filter((r): r is PromiseFulfilledResult<MergedSlot[]> => r.status === 'fulfilled')
      .flatMap((r) => r.value);

    // De-dupe by start time — when the same slot is offered by multiple fitters
    // (the common "Any Fitter" case), one of them takes it. Which one is the
    // mount's fitter-assignment rule (utils/fitterAssignment.ts):
    //
    //   random   uniformly at random. Previously this kept whichever fitter
    //            appeared first in the resource-list order, which loaded up the
    //            first fitter at each store with the entire Any-Fitter volume
    //            until they were fully booked; the random tiebreak distributes
    //            fittings fairly across all eligible fitters in the aggregate.
    //   popular  the fitter with the most fittings on record among those who
    //            have this time open, falling to the next most popular for the
    //            times the leader does not have.
    const byStart = new Map<string, MergedSlot[]>();
    for (const s of allSlots) {
      const arr = byStart.get(s.start);
      if (arr) arr.push(s);
      else byStart.set(s.start, [s]);
    }
    const mode = req.fitterAssignment ?? DEFAULT_FITTER_ASSIGNMENT;
    const countById = new Map(atLoc.map((f) => [f.id, f.fittingCount]));
    const deduped: MergedSlot[] = [];
    for (const candidates of byStart.values()) {
      deduped.push(pickFitterForSlot(candidates, mode, (id) => countById.get(id)));
    }

    return { slots: deduped.sort((a, b) => a.start.localeCompare(b.start)) };
  }

  throw new Error('getAvailability requires either resource or location');
}

// ─── Writes ─────────────────────────────────────────────────────────────────
// Schedule + Cancel cleared for sandbox testing 2026-05-24 (per Druv).
// See ~/.claude/projects/.../memory/project_schedule_cancel_unblocked.md

export async function createBooking(payload: BookingPayload): Promise<BookingResponse> {
  const res = await apiFetch<BookingResponse & { isSuccess?: boolean; message?: string }>(
    '/bookings',
    { method: 'POST', body: JSON.stringify(payload) },
  );
  // Double-booking guard. The Schedule endpoint rejects a customer who already
  // has a future appointment scheduled with HTTP 409 — apiFetch throws for that,
  // so the flow never reaches here. Belt-and-braces: if the middleware ever
  // relays the conflict as a 2xx carrying { isSuccess:false, message }, catch it
  // so the booking never advances to the confirmation screen. Throw with the
  // API's own message so the UI shows it (via apiErrorMessage).
  if (res && res.isSuccess === false) {
    const message =
      typeof res.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'This booking could not be completed.';
    throw new ApiError(409, message, JSON.stringify(res));
  }
  return res;
}

// Mirrors createBooking. The middleware endpoint is pending (Druv); on
// success returns a BookingResponse-shaped record with `id` prefixed
// `WL-` and `status: 'waitlisted'`. Called from TimeSlots' no-availability
// state so users have an actionable next step instead of a dead-end.
export async function createWaitlist(payload: WaitlistPayload): Promise<BookingResponse> {
  return apiFetch<BookingResponse>('/waitlist', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ─── Promo validation ──────────────────────────────────────────────────────
//
// Mirrors the booking call shape but returns validation + discount info
// only. The middleware endpoint is pending (Druv); calling this in advance
// of the endpoint landing will surface as a 404 from the middleware and
// the validate button will show a generic "couldn't validate" message.

export interface ValidatePromoRequest {
  promoCode: string;
  /** Store ID — required by SF /PromoValidate to scope the code to a store. */
  locationId: string;
  /** Fitting name — required to evaluate fitting-specific eligibility. */
  fittingTypeName: string;
  /** ISO slot start time — promo windows are checked against this. */
  appointmentStartDateTime: string;
  /** Cart subtotal before discount, in store currency. */
  price: number;
  /** Optional pre-pay subtotal when the store has prepayment configured. */
  prePayPrice?: number;
  /** Optional — enables Account-scoped, referral, or secondary codes. */
  email?: string;
}

export async function validatePromo(
  req: ValidatePromoRequest,
): Promise<ValidatePromoResponse> {
  return apiFetch<ValidatePromoResponse>('/promos/validate', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// Persists the post-booking GetSetUp form: ball fitting interest, phone,
// SMS opt-in, newsletter opt-in. Pending Druv. Failures don't block the
// user from completing the flow; the form surfaces the error inline and
// retains the entered data so the user can retry.
export async function submitPostBookingForm(payload: PostBookingPayload): Promise<void> {
  await apiFetch<unknown>('/post-booking', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ─── Reschedule (lookup + move) ────────────────────────────────────────────

/**
 * Look up a customer's upcoming appointments by name + email — the reschedule
 * flow's two-factor identity check. The middleware answers 404 identically
 * for "no upcoming appointment" and "identity mismatch" (deliberate — the
 * response must not confirm whether an email has an appointment). A 404
 * surfaces to callers as an ApiError with status 404, the same way
 * cancelBooking's callers detect it (see CancelLookup.tsx).
 */
export async function lookupAppointments(params: {
  email: string;
  name: string;
}): Promise<LookedUpAppointment[]> {
  const { appointments } = await apiFetch<{ appointments: LookedUpAppointment[] }>(
    `/bookings/lookup?email=${encodeURIComponent(params.email)}&name=${encodeURIComponent(params.name)}`,
  );
  return appointments ?? [];
}

/**
 * Move an existing appointment to a new slot (and optionally a new store /
 * service / fitter). Returns the same BookingResponse shape as POST /bookings.
 * Error mapping mirrors cancel: 404 not-found, 409 slot conflict, 400 other.
 * Callers strip any `-WL` suffix off the appointment number before calling
 * (display keeps the original).
 */
export async function rescheduleBooking(
  appointmentNumber: string,
  payload: ReschedulePayload,
): Promise<BookingResponse> {
  const res = await apiFetch<BookingResponse & { isSuccess?: boolean; message?: string }>(
    `/bookings/${encodeURIComponent(appointmentNumber)}/reschedule`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  // Same belt-and-braces guard as createBooking: if the middleware ever
  // relays an SF business rejection as a 2xx carrying { isSuccess:false },
  // refuse to advance to the confirmation screen.
  if (res && res.isSuccess === false) {
    const message =
      typeof res.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'This appointment could not be rescheduled.';
    throw new ApiError(409, message, JSON.stringify(res));
  }
  return res;
}

export async function cancelBooking(
  bookingId: string,
  customerHint?: { email?: string; phone?: string },
): Promise<void> {
  const params = new URLSearchParams();
  if (customerHint?.email) params.set('email', customerHint.email);
  if (customerHint?.phone) params.set('phone', customerHint.phone);
  const qs = params.toString();
  await apiFetch<void>(`/bookings/${encodeURIComponent(bookingId)}${qs ? `?${qs}` : ''}`, {
    method: 'DELETE',
  });
}
